import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Avatar,
    Chip,
    CircularProgress
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useNavigate } from 'react-router-dom';
import { getFlashBetSports, updateFlashBetSport } from '../Api';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import UpdateSportRequest from '../models/dtos/UpdateSportRequest';
import SportEditModal from '../components/SportEditModal';
import SportAiSettingsModal from '../components/SportAiSettingsModal';

const FlashBetSportsPage: React.FC = () => {
    const [sports, setSports] = useState<FlashBetSportDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [aiSettingsModalOpen, setAiSettingsModalOpen] = useState<boolean>(false);
    const [selectedSport, setSelectedSport] = useState<FlashBetSportDto | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadSports();
    }, []);

    const loadSports = async () => {
        setLoading(true);
        try {
            const result = await getFlashBetSports();
            setSports(result);
        } catch (error) {
            console.error('Error loading sports:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditSport = (sport: FlashBetSportDto) => {
        setSelectedSport(sport);
        setEditModalOpen(true);
    };

    const handleSaveEdit = async (sportData: UpdateSportRequest) => {
        if (!selectedSport) return;
        
        try {
            const result = await updateFlashBetSport(selectedSport.id, sportData);
            if (result) {
                setSports(result);
                setEditModalOpen(false);
                setSelectedSport(null);
            }
        } catch (error) {
            console.error('Error updating sport:', error);
        }
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedSport(null);
    };

    const handleOpenAiSettings = (sport: FlashBetSportDto) => {
        setSelectedSport(sport);
        setAiSettingsModalOpen(true);
    };

    const handleCloseAiSettings = () => {
        setAiSettingsModalOpen(false);
        setSelectedSport(null);
        // Reload sports list to reflect changes in AI prompt names
        loadSports();
    };

    const handleSportClick = (sport: FlashBetSportDto) => {
        navigate(`/flashbet/categories?sportIds=${sport.id}`);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                FlashBet Sports
            </Typography>
            
            <Grid container spacing={3}>
                {sports.map((sport) => (
                    <Grid item xs={12} sm={6} md={4} key={sport.id}>
                        <Card 
                            sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            <CardContent 
                                sx={{ flexGrow: 1 }}
                                onClick={() => handleSportClick(sport)}
                            >
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Avatar
                                        src={sport.photoUrl}
                                        alt={sport.name}
                                        sx={{ width: 60, height: 60, mr: 2 }}
                                    />
                                    <Box>
                                        <Typography variant="h6" component="h2">
                                            {sport.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            ID: {sport.id}
                                        </Typography>
                                        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                                            <Chip 
                                                label={sport.enabled ? 'Enabled' : 'Disabled'}
                                                color={sport.enabled ? 'success' : 'default'}
                                                size="small"
                                            />
                                            <Chip 
                                                icon={<AutoFixHighIcon sx={sport.aiPromptName ? { color: 'white' } : {}} />}
                                                label={sport.aiPromptName || 'Prompt not defined'}
                                                color={sport.aiPromptName ? 'primary' : 'warning'}
                                                size="small"
                                                variant={sport.aiPromptName ? 'filled' : 'filled'}
                                                sx={sport.aiPromptName ? { 
                                                    '& .MuiChip-label': { color: 'white' }
                                                } : {}}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                
                                {sport.localizations && Object.keys(sport.localizations).length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Localizations:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {Object.entries(sport.localizations).map(([lang, name]) => (
                                                <Chip 
                                                    key={lang}
                                                    label={`${lang}: ${name}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                            
                            <CardActions>
                                <Button 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSport(sport);
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button 
                                    size="small" 
                                    onClick={() => handleSportClick(sport)}
                                >
                                    Categories
                                </Button>
                                <Button 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAiSettings(sport);
                                    }}
                                >
                                    AI Settings
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <SportEditModal
                open={editModalOpen}
                sport={selectedSport}
                onSave={handleSaveEdit}
                onClose={handleCloseModal}
            />

            <SportAiSettingsModal
                open={aiSettingsModalOpen}
                sport={selectedSport}
                onClose={handleCloseAiSettings}
            />
        </Container>
    );
};

export default FlashBetSportsPage;